import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { compose } from "redux";
import { CampaignType, CampaignCategory } from "pages/dealScreener/Email/constants";
import styled from "styled-components/macro";
import { CircularProgress } from "@material-ui/core";
import _ from "lodash";
import { ROUTE_CAMPAIGNS } from "constants/routes";
import {
  getPrepayment,
  getValuationSensitivity,
  toggleCampaignEmailIsComposedFlag,
  getEmailCampaign,
  updateEmailCampaign,
  toggleDealCampaign,
  resetCampaign,
  updateEmailCampaignEmail,
  resetEmailCampaignPrepayment,
  resetEmailCampaignValuationSensitivity
} from "pages/campaigns/emailCampaign.store";
import { LBFooter } from "components";
import { format } from "date-fns";
import { mapCompanySelector } from "store/state/auth";

import UserConfirmationModal from "../../../modals/UserConfirmationModal/UserConfirmationModal";
import {
  peopleGetContactsByCompany
} from "../../../modules/people/people.store";

import {
  EdcEmailComposition,
  EdcProgressBanner,
  PORTFOLIO_STEPS,
  DEAL_STEPS,
  getCampaignTypesConfig,
  DEFAULT_COMPANY_DROPDOWN_OPTIONS,
  ReviewAndSend,
  EmailRecipientDetails
} from "./";

export const PAGE_PADDING_WIDTH = "4em";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2em;
  background-color: #FFFFFF;
`;

const CenteredBodyDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1em ${PAGE_PADDING_WIDTH};
`;

const FooterDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1em ${PAGE_PADDING_WIDTH};
  background-color: #FAFAFA;
`;

const IndividualCampaign = ({
  match,
  campaign,
  canAddOrRemoveDeals,
  getEmailCampaign,
  updateEmailCampaign,
  history,
  toggleDealCampaign: toggleDealCampaignService,
  getPrepayment: getPrepaymentService,
  getValuationSensitivity: getValuationSensitivityService,
  company,
  toggleCampaignEmailIsComposedFlag: toggleCampaignEmailIsComposedService,
  updateEmailCampaignEmail: updateEmailCampaignEmailService,
  peopleGetContactsByCompany: peopleGetContactsByCompanyService,
  resetCampaign,
  brokerContacts,
  resetEmailCampaignPrepayment,
  resetEmailCampaignValuationSensitivity
}) => {
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(true);
  const [campaignType, setCampaignType] = useState(null);
  const [campaignCategoryState, setCampaignCategoryState] = useState(null);
  const [editCampaignNameModalIsOpen, setEditCampaignNameModalIsOpen] = useState(false);
  const [editedCampaignName, setEditedCampaignName] = useState("");
  const [campaignCreatedAt, setCampaignCreatedAt] = useState("");
  const [campaignSteps, setCampaignSteps] = useState([]);
  const [campaignConfig, setCampaignConfig] = useState({});
  const [baseDataset, setBaseDataSet] = useState([]);
  const [visibleDataset, setVisibleDataset] = useState([]);
  const [search, setSearch] = useState("");
  const [activeStep, setActiveStep] = useState(null);
  const [filterCompanies, setFilterCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState(-1);
  const [reviewAndSendRows, setReviewAndSendRows] = useState([]);
  const [emailRecipientDetailsRows, setEmailRecipientDetailsRows] = useState([]);
  const [email, setEmail] = useState({});
  const [contactOptions, setContactOptions] = useState([]);
  const [baseEmailCompHash, setBaseEmailCompHash] = useState("");
  const [savingEmailCompHash, setSavingEmailCompHash] = useState("");
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [nextRoute, setNextRoute] = useState("");

  const campaignId = match.params.campaignId;
  const campaignName = campaign?.name ?? "Mass Email";
  const campaignCategory = Object.values(CampaignType)?.find(ct => ct?.id === campaign?.campaignTypeId)?.categoryId;
  const isPortfolioCampaign = campaignCategory === CampaignCategory.PORTFOLIO.id;
  const isDealCampaign = campaignCategory === CampaignCategory.DEAL.id;
  const cameFromDealScreener = history.location?.state?.from === `/dealScreener/${campaignId}`;
  const ENABLED_DEAL_STEPS = DEAL_STEPS({ isDisabled: false });
  const DISABLED_DEAL_STEPS = DEAL_STEPS({ isDisabled: true });

  useEffect(() => {
    getEmailCampaign({ campaignId });
    peopleGetContactsByCompanyService({ companyId: company.id });
  }, []);

  useEffect(() => {
    const options = brokerContacts?.map(({ id, firstName, lastName }) => ({
      value: id,
      label: `${firstName} ${lastName}`
    }));
    setContactOptions(options);
  }, [brokerContacts]);

  useEffect(() => {
    // Placeholder for when email recipient data starts coming in. 20220923 TDL
  }, [emailRecipientDetailsRows]);

  useEffect(() => {
    if (!_.isEmpty(campaign)) {
      const {
        createdAt,
        campaignTypeId,
        isEmailComposed
      } = campaign;
      
      setIsLoadingCampaign(false);

      setCampaignCreatedAt(createdAt ? `Created on ${format(new Date(createdAt ?? new Date()), "MMMM dd, yyyy")}` : "");
      setCampaignType(campaignTypeId ?? null);
      setCampaignCategoryState(campaignCategory);
      setEditedCampaignName(campaignName);

      const _data = campaign?.deals ?? [];
      const sortedDataAlphabetically = _data
        .sort((a, b) => a.sponsorCompanyName.toLowerCase().localeCompare(b.sponsorCompanyName.toLowerCase()) ||
          a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      setBaseDataSet(sortedDataAlphabetically);
      setVisibleDataset(sortedDataAlphabetically);

      if (isPortfolioCampaign) {
        setCampaignSteps(PORTFOLIO_STEPS);
        setActiveStep(0);
      } else if (isDealCampaign && !cameFromDealScreener && _data.length === 0 && !campaign.isEmailComposed) {
        setCampaignSteps(isEmailComposed ? ENABLED_DEAL_STEPS : DISABLED_DEAL_STEPS);
        setActiveStep(0);
      } else if ((isDealCampaign && cameFromDealScreener) || _data.length > 0) {
        setCampaignSteps(isEmailComposed ? ENABLED_DEAL_STEPS : DISABLED_DEAL_STEPS);
        setActiveStep(isEmailComposed ? 2 : 1);
      } else {
        setCampaignSteps(isEmailComposed ? ENABLED_DEAL_STEPS : DISABLED_DEAL_STEPS);
      }

      const config = getCampaignTypesConfig(campaign?.campaignTypeId, campaignCategory);
      setCampaignConfig(config);
      const composition = campaign.emailComposition;
      if (composition) {
        setEmail(composition);
        const hash = `${composition.from}${composition.subject}${composition.textBody}${composition.isCcBrokerIncluded}`;
        setBaseEmailCompHash(hash);
        setSavingEmailCompHash(hash);
      } else {
        setEmail({});
        setBaseEmailCompHash("");
        setSavingEmailCompHash("");
      }
    }
  }, [campaign]);

  useEffect(() => {
    if (email?.from || email?.subject || email?.textBody) {
      const hash = `${email.from}${email.subject}${email.textBody}${email.isCcBrokerIncluded}`;
      setSavingEmailCompHash(hash);
    }
  }, [email]);

  const setAndCheckActiveStep = step => {
    if (step === 2) {
      if (baseEmailCompHash !== savingEmailCompHash) {
        // Mismatch - open the prompt;
        setConfirmationModalOpen(true);
      } else {
        setFilterCompany(-1);
        setActiveStep(step);
      }
    } else {
      setFilterCompany(-1);
      setActiveStep(step);
    }
  };

  const confirm = () => {
    if (nextRoute) {
      updateEmailCampaignEmailService({ campaignId, email }, false);
      resetCampaign();
      history.push(nextRoute, { from: history.location.pathname });
    } else {
      handleComposeEmailSaveAndContinueClicked();
      setConfirmationModalOpen(false);
    }
  };

  const cancel = () => {
    if (nextRoute) {
      resetCampaign();
      history.push(nextRoute, { from: history.location.pathname });
    } else {
      getEmailCampaign({ campaignId });
      setActiveStep(2);
      setConfirmationModalOpen(false);
    }
  };

  useEffect(() => {
    if (campaignConfig.searchFunction) {
      setVisibleDataset(campaignConfig?.searchFunction(baseDataset, search, filterCompany));
    }
  }, [search, filterCompany]);

  useEffect(() => {
    const rows = visibleDataset.map(deal => ({
      ...deal?.currentDeal,
      ...deal?.currentDeal?.prepaymentPenalty,
      ...deal
    }));

    setReviewAndSendRows(rows);

    // PLACEHOLDER FOR EMAIL RECIPIENT DATA
    setEmailRecipientDetailsRows([]);
  }, [visibleDataset, setReviewAndSendRows, setEmailRecipientDetailsRows]);

  useEffect(() => {
    if (baseDataset.length === 0) {
      setFilterCompanies([DEFAULT_COMPANY_DROPDOWN_OPTIONS]);
    }

    if (baseDataset.length > 0) {
      const companies = baseDataset.map(({ sponsorCompanyId, sponsorCompanyName }) => ({ id: sponsorCompanyId, label: sponsorCompanyName }));
      const uniqueCompaniesInList = companies.filter((elem, index) => companies.findIndex(obj => obj.id === elem.id) === index);
      setFilterCompanies([DEFAULT_COMPANY_DROPDOWN_OPTIONS, ...uniqueCompaniesInList]);
    }
  }, [baseDataset, setFilterCompanies]);

  const handleEditNameIconClick = () => setEditCampaignNameModalIsOpen(true);
  const handleOnCancelClicked = () => setEditCampaignNameModalIsOpen(false);
  const handleOnSaveClicked = () => {
    setEditCampaignNameModalIsOpen(false);
    if (editedCampaignName !== campaignName) {
      const newValues = {
        ...campaign,
        name: editedCampaignName
      };
      updateEmailCampaign({ campaignId, newValues, callback: () => getEmailCampaign({ campaignId }) });
    }
  };

  const toggleElement = id => {
    // Optimistic UI Update
    const toggledCampaignActiveState = campaign.deals.map(deal => ((deal.id === id) ?
      ({
        ...deal,
        activeForCampaign: !deal.activeForCampaign
      }) :
      ({ ...deal })));

    setVisibleDataset(toggledCampaignActiveState);

    // Need to figure out a better solution to the toggle update api call
    toggleDealCampaignService({ dealId: id, campaignId });
  };

  const handleComposeEmailSaveAndContinueClicked = () => {
    setCampaignSteps(ENABLED_DEAL_STEPS);
    toggleCampaignEmailIsComposedService({ campaignId });
    updateEmailCampaignEmailService({ campaignId, email }, true);
    setBaseEmailCompHash(savingEmailCompHash);
    setActiveStep(2);
  };

  const handleComposeEmailSaveAndExitClicked = () => {
    setBaseEmailCompHash(savingEmailCompHash);
    updateEmailCampaignEmailService({ campaignId, email }, false);
    toggleCampaignEmailIsComposedService({ campaignId });
    resetCampaign();
    history.push(ROUTE_CAMPAIGNS, { from: history.location.pathname });
  };

  const backToDashboardClick = () => {
    if (baseEmailCompHash !== savingEmailCompHash) {
      setConfirmationModalOpen(true);
      setNextRoute(ROUTE_CAMPAIGNS);
    } else {
      resetCampaign();
      history.push(ROUTE_CAMPAIGNS, { from: history.location.pathname });
    }
  };

  return (
    <>
      {(_.isEmpty(campaign) || !campaignType || !campaignCategoryState) || <div />}
      {
        <PageContainer>
          <EdcProgressBanner
            resetCampaign={resetCampaign}
            shouldResetCampaign={baseEmailCompHash === savingEmailCompHash}
            handleEditNameIconClick={handleEditNameIconClick}
            backToDashboardClick={backToDashboardClick}
            history={history}
            name={campaignName}
            createdAt={campaignCreatedAt}
            campaignSteps={campaignSteps}
            activeStep={activeStep}
            setActiveStep={setAndCheckActiveStep}
            campaignCategory={campaignCategoryState}
          />
          <CenteredBodyDiv>
            {isLoadingCampaign && <CircularProgress />}
            {
              isDealCampaign && activeStep === 1 &&
              /*
               * This is a temporary placeholder of the email step until we work on the updated one
               * Should look at changing over to page/sub-page setup instead of keeping state logic about which
               * step the user is on.
               */
              <>
                <EdcEmailComposition
                  handleComposeEmailSaveAndExitClicked={handleComposeEmailSaveAndExitClicked}
                  handleComposeEmailSaveAndContinueClicked={handleComposeEmailSaveAndContinueClicked}
                  company={company}
                  campaignType={campaignType}
                  email={email}
                  setEmail={setEmail}
                  contactOptions={contactOptions}
                />
                <UserConfirmationModal
                  onConfirmClick={confirm}
                  onCancelClick={cancel}
                  onClose={() => {
                    setConfirmationModalOpen(false);
                  }}
                  open={confirmationModalOpen}
                />
              </>
            }
            {
              isDealCampaign && activeStep === 2 &&
              <ReviewAndSend
                setSearch={setSearch}
                filterCompany={filterCompany}
                setFilterCompany={setFilterCompany}
                filterCompanies={filterCompanies}
                campaignConfig={campaignConfig}
                isLoading={isLoadingCampaign}
                rows={reviewAndSendRows}
                toggleElement={toggleElement}
                editedCampaignName={editedCampaignName}
                setEditedCampaignName={setEditedCampaignName}
                editCampaignNameModalIsOpen={editCampaignNameModalIsOpen}
                onCancelClicked={handleOnCancelClicked}
                onSaveClicked={handleOnSaveClicked}
                getPrepayment={getPrepaymentService}
                getValuationSensitivity={getValuationSensitivityService}
                resetEmailCampaignPrepayment={resetEmailCampaignPrepayment}
                resetEmailCampaignValuationSensitivity={resetEmailCampaignValuationSensitivity}
                company={company}
                campaignType={campaignType}
                canAddOrRemoveDeals={canAddOrRemoveDeals}
              />
            }
            {
              isDealCampaign && activeStep === 3 &&
              <EmailRecipientDetails
                filterCompany={filterCompany}
                filterCompanies={filterCompanies}
                onFilterCompany={setFilterCompany}
              />
            }
          </CenteredBodyDiv>
          <FooterDiv>
            <LBFooter />
          </FooterDiv>
        </PageContainer>
      }
    </>
  );
};

const mapStateToProps = ({ authentication, peopleModule, emailCampaign }) => ({
  company: mapCompanySelector(authentication),
  brokerEmail: authentication.user.contact.email,
  brokerContacts: peopleModule.peopleModuleReducer.companyContacts,
  ...emailCampaign
});

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    {
      getEmailCampaign,
      updateEmailCampaign,
      toggleDealCampaign,
      getPrepayment,
      getValuationSensitivity,
      toggleCampaignEmailIsComposedFlag,
      resetCampaign,
      updateEmailCampaignEmail,
      peopleGetContactsByCompany,
      resetEmailCampaignPrepayment,
      resetEmailCampaignValuationSensitivity
    }
  )
)(IndividualCampaign);
